import { Play } from "lucide-react";
import { useRef, useState } from "react";
import { IconRenderer } from "~/components/IconRenderer";
import { RichTextRenderer } from "~/components/RichTextRenderer";
import { cn } from "~/lib/utils";
import { LayoutGridTwoCols } from "./Layout/LayoutGridTwoCols";

export const MultiStepCompare = ({
  steps,
}: {
  steps: {
    id: string;
    title: string;
    description: string;
    icon: string;
    video?: string;
    videoPoster: string;
    videoPosterCrop: string;
  }[];
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const [videoKey, setVideoKey] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlayback = () => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const crop = steps[currentStep]?.videoPosterCrop
    ? JSON.parse(steps[currentStep].videoPosterCrop).default.cropArea
    : { x: 0, y: 0, width: 1, height: 1 };

  const getProcessedPosterUrl = (videoPoster: string) => {
    if (!videoPoster) return videoPoster;

    // If crop values are default (0, 0, 1, 1), no cropping needed, return original
    if (crop.x === 0 && crop.y === 0 && crop.width === 1 && crop.height === 1) {
      return videoPoster;
    }

    // extract the /fileadmin part from the videoPoster URL
    const fileadminMatch = videoPoster.match(/\/fileadmin\/[^?]*/);
    if (!fileadminMatch) return videoPoster;

    const filePath = fileadminMatch[0];

    // extract base URL from VITE_ASSET_URL or from videoPoster
    const baseUrl = import.meta.env.VITE_ASSET_URL
      ? new URL(import.meta.env.VITE_ASSET_URL).origin
      : new URL(videoPoster).origin;

    // construct processed URL
    const processedUrl = new URL(`${baseUrl}/api/locationguide/processed`);
    processedUrl.searchParams.set("file", filePath);
    processedUrl.searchParams.set("x", String(crop.x));
    processedUrl.searchParams.set("y", String(crop.y));
    processedUrl.searchParams.set("width", String(crop.width));
    processedUrl.searchParams.set("height", String(crop.height));

    return processedUrl.toString();
  };

  const processedPoster = steps[currentStep]?.videoPoster
    ? getProcessedPosterUrl(steps[currentStep].videoPoster)
    : steps[currentStep]?.videoPoster;
  const currentVideo = steps[currentStep]?.video;
  const hasVideo = Boolean(currentVideo);

  return (
    <LayoutGridTwoCols>
      <div
        className={cn("relative aspect-video rounded-md", hasVideo ? "cursor-pointer" : "cursor-default")}
        onClick={hasVideo ? togglePlayback : undefined}
      >
        {hasVideo ? (
          <video
            controls={isPlaying}
            key={`${currentStep}-${videoKey}`}
            ref={videoRef}
            src={currentVideo}
            poster={processedPoster}
            className="w-full h-full object-contain rounded-md"
            onEnded={() => {
              setIsPlaying(false);
              setVideoKey((k) => k + 1);
            }}
          ></video>
        ) : (
          <img
            src={processedPoster}
            alt={steps[currentStep]?.title || "Step preview"}
            className="w-full h-full object-contain rounded-md"
          />
        )}
        {hasVideo && !isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-md">
            <div className="rounded-full bg-white size-16 flex items-center justify-center">
              <Play className="text-black" />
            </div>
          </div>
        )}
      </div>
      <div>
        <div className="flex flex-col gap-12 grow-0 relative">
          {steps.map((step, index) => {
            return (
              <div key={step.id} className="relative">
                <Step
                  {...step}
                  isActiveStep={index === currentStep}
                  isLastStep={index === steps.length - 1}
                  onClick={() => {
                    setCurrentStep(index);
                    setIsPlaying(false);
                  }}
                />
                {index !== steps.length - 1 && (
                  // Linie nur, wenn es nicht der letzte Step ist
                  <div className="absolute top-full left-[27px] h-[calc(100%+12px)] w-[2px] bg-black"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </LayoutGridTwoCols>
  );
};

const Step = ({
  id,
  title,
  description,
  icon,
  isActiveStep,
  isLastStep,
  onClick,
}: {
  id: string;
  title: string;
  description: string;
  icon: string;
  isActiveStep: boolean;
  isLastStep: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      key={id}
      className="flex gap-12 text-left cursor-pointer"
      onClick={onClick}
    >
      <div className="[&_svg]:shrink-0 [&_svg]:size-8 relative">
        {!isLastStep && (
          <div className="absolute top-0 left-[27px] h-full w-[2px] bg-black"></div>
        )}
        <div
          className={cn(
            "relative z-0 p-3 rounded-md transition-all duration-700 ease-out",
            isActiveStep ? "bg-black text-primary" : "bg-white"
          )}
        >
          <IconRenderer icon={icon.split("-")[1]} />
        </div>
      </div>
      <div>
        <h3 className="font-medium text-xl pt-3">{title}</h3>
        {isActiveStep && (
          <RichTextRenderer additionalClasses="mt-3" richText={description} />
        )}
      </div>
    </button>
  );
};
